import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import {
  EhDataTableShell,
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  useTableSort,
} from "@/components/dataTable";
import { Spinner } from "@/components/ui/Spinner";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { getAuftragSharepointWebUrl } from "@/lib/projects";
import { toast } from "@/lib/toast";
import {
  formatProjectCompletionAuftragLabel,
  formatProjectCompletionDateTime,
  getProjectCompletionFolderPath,
  getProjectCompletionStatusMeta,
  projectCompletionHasFolder,
  projectCompletionStatusBadgeClass,
  type ProjectCompletionDoc,
  type ProjectCompletionRow,
} from "@/lib/projectCompletions";
import {
  clampProjectCompletionColumnWidth,
  getProjectCompletionColumnDefaultWidth,
  getProjectCompletionColumnLabel,
  isProjectCompletionActionColumn,
  PROJECT_COMPLETION_COLUMNS,
  type ProjectCompletionColumnKey,
} from "@/pages/stundennachweise/projectCompletionColumns";
import { getTimesheetActionColWidth } from "@/pages/stundennachweise/columns";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";

const HERO_PROJECT_BASE = "https://login.hero-software.de/partner/Projects/view/";
const HERO_LOGO_LIGHT = "/static/img/hero-logo.png";
const HERO_LOGO_DARK = "/static/img/hero-logo-dark.png";
const HERO_LEGACY_LOGO_LIGHT = "/static/img/plancraft-logo.png";
const HERO_LEGACY_LOGO_DARK = "/static/img/plancraft-logo-dark.png";

export interface ProjectCompletionsTableProps {
  rows: ProjectCompletionRow[];
  loading: boolean;
  showElectronFolder: boolean;
  emptyText?: string;
}

export function ProjectCompletionsTable({
  rows,
  loading,
  showElectronFolder,
  emptyText = "Für die aktuelle Auswahl wurden keine Fertigstellungsmeldungen gefunden.",
}: ProjectCompletionsTableProps) {
  return (
    <>
      <div className="hidden md:block">
        <DesktopTable
          rows={rows}
          loading={loading}
          showElectronFolder={showElectronFolder}
          emptyText={emptyText}
        />
      </div>
      <div className="md:hidden">
        <MobileCards
          rows={rows}
          loading={loading}
          showElectronFolder={showElectronFolder}
          emptyText={emptyText}
        />
      </div>
    </>
  );
}

interface CompletionDesktopTheadProps {
  dataColumns: ProjectCompletionColumnKey[];
  actionColumns: ProjectCompletionColumnKey[];
  actionColWidth: number;
  actionRightOffsets: Record<string, number>;
  showElectronFolder: boolean;
  sort: { key: string; direction: "asc" | "desc" } | null;
  onSort: (key: string) => void;
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  omitResizers: boolean;
}

function CompletionDesktopThead({
  dataColumns,
  actionColumns,
  actionColWidth,
  actionRightOffsets,
  showElectronFolder,
  sort,
  onSort,
  activeResize,
  onResizePointerDown,
  omitResizers,
}: CompletionDesktopTheadProps) {
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {dataColumns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3 py-2 text-left font-semibold leading-snug"
          >
            <SortableHeader
              label={getProjectCompletionColumnLabel(col)}
              columnKey={col}
              sort={sort}
              onSort={onSort}
            />
            {!omitResizers && (
              <span
                className={clsx("eh-data-resizer", activeResize === col && "is-active")}
                role="separator"
                aria-orientation="vertical"
                title="Spaltenbreite ziehen"
                onPointerDown={(e) => onResizePointerDown(e, col, idx)}
              />
            )}
          </th>
        ))}
        {actionColumns.map((col) => (
          <th
            key={col}
            scope="col"
            className="eh-data-action-cell whitespace-nowrap py-2 text-center text-[10px] font-semibold uppercase tracking-wide"
            style={{ width: `${actionColWidth}px`, right: `${actionRightOffsets[col]}px` }}
            title={getProjectCompletionColumnLabel(col)}
          >
            <span className="sr-only">{getProjectCompletionColumnLabel(col)}</span>
            <span
              className="inline-flex items-center justify-center gap-0"
              style={{ width: `${actionColWidth}px` }}
              aria-hidden
            >
              <span className="inline-flex w-11 justify-center">
                <img
                  src="/static/icons/apple-touch-icon-180.png"
                  alt=""
                  width={18}
                  height={18}
                  className="object-contain opacity-70"
                  decoding="async"
                />
              </span>
              {showElectronFolder ? (
                <span className="inline-flex w-11 justify-center text-[1.25rem] leading-none opacity-70">
                  📂
                </span>
              ) : null}
              <span className="inline-flex w-11 justify-center">
                <img
                  src={HERO_LOGO_LIGHT}
                  alt=""
                  width={22}
                  height={22}
                  className="object-contain opacity-70 dark:hidden"
                  decoding="async"
                />
                <img
                  src={HERO_LOGO_DARK}
                  alt=""
                  width={22}
                  height={22}
                  className="hidden object-contain opacity-70 dark:block"
                  decoding="async"
                />
              </span>
            </span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

function DesktopTable({
  rows,
  loading,
  showElectronFolder,
  emptyText,
}: {
  rows: ProjectCompletionRow[];
  loading: boolean;
  showElectronFolder: boolean;
  emptyText: string;
}) {
  const columns = PROJECT_COMPLETION_COLUMNS;
  const dataColumns = useMemo(
    () => columns.filter((c) => !isProjectCompletionActionColumn(c)),
    [columns],
  );
  const actionColumns = useMemo(
    () => columns.filter((c) => isProjectCompletionActionColumn(c)),
    [columns],
  );
  const { sort, toggle, reset } = useTableSort();
  const sortedRows = useMemo(
    () => sortRowsByKey(rows, sort, getProjectCompletionSortValue),
    [rows, sort],
  );

  const [widths, setWidths] = useState<Record<string, number>>({});

  const dataWidths = useMemo(() => {
    return dataColumns.map((col) => {
      const stored = Number(widths[col]);
      const defaultWidth = getProjectCompletionColumnDefaultWidth(col);
      return Number.isFinite(stored) && stored > 0
        ? clampProjectCompletionColumnWidth(stored)
        : defaultWidth;
    });
  }, [dataColumns, widths]);

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);

  const actionColWidth = useMemo(
    () => getTimesheetActionColWidth(showElectronFolder),
    [showElectronFolder],
  );

  const actionRightOffsets = useMemo(() => {
    const offsets: Record<string, number> = {};
    let right = 0;
    for (let i = actionColumns.length - 1; i >= 0; i--) {
      const key = actionColumns[i];
      if (!key) continue;
      offsets[key] = right;
      right += actionColWidth;
    }
    return offsets;
  }, [actionColumns, actionColWidth]);

  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () =>
      [dataColumns.join("|"), actionColumns.join("|"), dataWidths.join(","), rows.length].join(
        "§",
      ),
    [dataColumns, actionColumns, dataWidths, rows.length],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: dataColumns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? getProjectCompletionColumnDefaultWidth(colKey);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampProjectCompletionColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) {
        setWidths((prev) => ({ ...prev, [colKey]: lastWidth }));
      }
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={reset} />
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {dataColumns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
            {actionColumns.map((col) => (
              <col key={col} style={{ width: `${actionColWidth}px` }} />
            ))}
          </colgroup>

          <CompletionDesktopThead
            dataColumns={dataColumns}
            actionColumns={actionColumns}
            actionColWidth={actionColWidth}
            actionRightOffsets={actionRightOffsets}
            showElectronFolder={showElectronFolder}
            sort={sort}
            onSort={toggle}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            omitResizers={false}
          />

          <tbody className="divide-y divide-separator bg-card">
            {loading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-10 text-center text-label-secondary"
                >
                  <Spinner className="mx-auto" />
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-label-tertiary"
                >
                  {emptyText}
                </td>
              </tr>
            ) : null}
            {sortedRows.map((row) => (
              <tr
                key={String(row.id ?? formatProjectCompletionAuftragLabel(row))}
                className="eh-data-row align-middle transition"
              >
                {dataColumns.map((col) => (
                  <td
                    key={col}
                    className="eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-2 text-label"
                  >
                    <CellContent col={col} row={row} />
                  </td>
                ))}
                {actionColumns.map((col) => (
                  <td
                    key={col}
                    className="eh-data-action-cell align-middle p-0 text-center leading-none"
                    style={{
                      width: `${actionColWidth}px`,
                      right: `${actionRightOffsets[col]}px`,
                    }}
                  >
                    <ActionCell row={row} showElectronFolder={showElectronFolder} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{
              top: floating.top,
              left: floating.left,
              width: floating.width,
            }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {dataColumns.map((col, idx) => (
                    <col
                      key={col}
                      ref={(el) => {
                        floatingColRefs.current[col] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                  {actionColumns.map((col) => (
                    <col key={col} style={{ width: `${actionColWidth}px` }} />
                  ))}
                </colgroup>
                <CompletionDesktopThead
                  dataColumns={dataColumns}
                  actionColumns={actionColumns}
                  actionColWidth={actionColWidth}
                  actionRightOffsets={actionRightOffsets}
                  showElectronFolder={showElectronFolder}
                  sort={sort}
                  onSort={toggle}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  omitResizers={false}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function getProjectCompletionSortValue(
  row: ProjectCompletionRow,
  col: string,
): string | number | null | undefined {
  switch (col) {
    case "auftrag":
      return formatProjectCompletionAuftragLabel(row);
    case "kunde":
      return row.kunde;
    case "status":
      return getProjectCompletionStatusMeta(row).label;
    case "completed_by":
      return row.project_completion_created_by;
    case "completed_at":
      return row.project_completion_created_at;
    case "delivery_notes":
      return row.delivery_notes
        ?.map((doc) => String(doc.number || doc.name || doc.id || "").trim())
        .join(", ");
    case "invoices":
      return row.invoices
        ?.map((doc) => String(doc.number || doc.name || doc.id || "").trim())
        .join(", ");
    default:
      return null;
  }
}

function CellContent({
  col,
  row,
}: {
  col: ProjectCompletionColumnKey;
  row: ProjectCompletionRow;
}) {
  switch (col) {
    case "auftrag":
      return (
        <span className="font-medium break-words">
          {formatProjectCompletionAuftragLabel(row)}
        </span>
      );
    case "kunde":
      return <span>{row.kunde || "—"}</span>;
    case "status":
      return <StatusBadge row={row} />;
    case "completed_by":
      return <span>{row.project_completion_created_by || "—"}</span>;
    case "completed_at":
      return (
        <span className="whitespace-nowrap tabular-nums">
          {formatProjectCompletionDateTime(row.project_completion_created_at)}
        </span>
      );
    case "delivery_notes":
      return <DocLinks docs={row.delivery_notes} />;
    case "invoices":
      return <DocLinks docs={row.invoices} />;
    default:
      return <span>—</span>;
  }
}

function MobileCards({
  rows,
  loading,
  showElectronFolder,
  emptyText,
}: {
  rows: ProjectCompletionRow[];
  loading: boolean;
  showElectronFolder: boolean;
  emptyText: string;
}) {
  if (loading && !rows.length) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <p className="rounded-ios-lg bg-fill-quaternary/25 px-4 py-8 text-center text-sm text-label-secondary ring-1 ring-separator/30">
        {emptyText}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-separator rounded-ios-lg bg-card ring-1 ring-separator/35">
      {rows.map((row) => (
        <li key={String(row.id ?? formatProjectCompletionAuftragLabel(row))} className="px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              <p className="font-semibold text-label">{formatProjectCompletionAuftragLabel(row)}</p>
              <p className="text-sm text-label-secondary">{row.kunde || "—"}</p>
            </div>
            <StatusBadge row={row} />
          </div>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-label-secondary">
            <dt>Fertiggestellt von</dt>
            <dd>{row.project_completion_created_by || "—"}</dd>
            <dt>Fertiggestellt am</dt>
            <dd>{formatProjectCompletionDateTime(row.project_completion_created_at)}</dd>
            <dt>Lieferscheine</dt>
            <dd>
              <DocLinks docs={row.delivery_notes} />
            </dd>
            <dt>Rechnungen</dt>
            <dd>
              <DocLinks docs={row.invoices} />
            </dd>
          </dl>
          <div className="mt-3 flex justify-end">
            <ActionCell row={row} showElectronFolder={showElectronFolder} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ row }: { row: ProjectCompletionRow }) {
  const status = getProjectCompletionStatusMeta(row);
  return (
    <TimesheetBadge className={projectCompletionStatusBadgeClass(status.key)} compact>
      {status.label}
    </TimesheetBadge>
  );
}

function DocLinks({ docs }: { docs: ProjectCompletionDoc[] | undefined }) {
  if (!Array.isArray(docs) || !docs.length) {
    return <span className="text-label-tertiary">—</span>;
  }

  return (
    <ul className="space-y-0.5">
      {docs.map((doc, index) => {
        const label = String(doc?.number || doc?.name || doc?.id || "Dokument").trim();
        const url = String(doc?.web_url || "").trim();
        return (
          <li key={String(doc?.id ?? `${label}-${index}`)}>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {label}
              </a>
            ) : (
              <span>{label}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ActionCell({
  row,
  showElectronFolder,
}: {
  row: ProjectCompletionRow;
  showElectronFolder: boolean;
}) {
  const isDark = useIsDarkTheme();
  const hasFolder = projectCompletionHasFolder(row);
  const heroId = row.hero_project_id;
  const heroSrc = isDark ? HERO_LOGO_DARK : HERO_LOGO_LIGHT;

  return (
    <div className="inline-flex items-center justify-center">
      <ProjectOverviewButton
        auftragId={row.id}
        className="eh-data-action-btn shrink-0 rounded-lg border-0 bg-transparent shadow-none"
      />
      {showElectronFolder ? (
        hasFolder ? (
          <button
            type="button"
            className="eh-data-action-btn shrink-0"
            title="Ordner öffnen"
            aria-label="Ordner öffnen"
            onClick={(e) => void handleOpenFolder(e, row)}
          >
            <span className="text-[1.25rem] leading-none" aria-hidden>
              📂
            </span>
          </button>
        ) : (
          <span
            title="Kein Projektordner hinterlegt"
            className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
          >
            <span className="text-[1.25rem] leading-none opacity-35 grayscale" aria-hidden>
              📂
            </span>
          </span>
        )
      ) : null}
      {heroId ? (
        <a
          href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(heroId))}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Hero-Projekt öffnen"
          className="eh-data-action-btn shrink-0"
        >
          <img
            src={heroSrc}
            alt="Hero"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
        </a>
      ) : (
        <span
          title="Kein Hero-Projekt hinterlegt"
          className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
          style={{ opacity: 0.5 }}
        >
          <img
            src={isDark ? HERO_LEGACY_LOGO_DARK : HERO_LEGACY_LOGO_LIGHT}
            alt="Hero"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
        </span>
      )}
    </div>
  );
}

function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() => readIsDark());
  useEffect(() => {
    const root = document.documentElement;
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => setIsDark(readIsDark()));
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme", "class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function readIsDark(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "dark") return true;
  if (root.classList.contains("dark")) return true;
  return false;
}

async function handleOpenFolder(e: React.MouseEvent, row: ProjectCompletionRow) {
  e.stopPropagation();
  const path = getProjectCompletionFolderPath(row);
  if (!path) {
    if (row.id) {
      const webUrl = await getAuftragSharepointWebUrl(Number(row.id));
      if (webUrl) window.open(webUrl, "_blank", "noopener");
    }
    return;
  }
  const openFolder = window.envAPI?.openFolder;
  if (typeof openFolder !== "function") return;
  const err = await openFolder(path);
  if (err) {
    if (row.id) {
      const webUrl = await getAuftragSharepointWebUrl(Number(row.id));
      if (webUrl) {
        window.open(webUrl, "_blank", "noopener");
        return;
      }
    }
    toast.error(String(err));
  }
}

export function projectCompletionsShowElectronFolder(userIsBuero: boolean): boolean {
  return userIsBuero && isElectronWithOpenFolder();
}
